import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import LanguageDropdown from './LanguageDropdown';
import { useLanguage } from './LanguageContext';

const Nav = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100px;
  padding: 0 2rem;
  background: rgba(248, 249, 250, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--background-dark);
  z-index: 1000;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-left: auto;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
  margin-right: 2.5rem;
  background: transparent;
  backdrop-filter: none;

  @media (max-width: 768px) {
    margin-right: 1rem;
  }

  img {
    height: 90px;
    width: auto;
    transition: transform 0.3s ease;
    background: transparent;
    opacity: 0.9;

    @media (max-width: 1024px) {
      height: 75px;
    }
    
    @media (max-width: 768px) {
      height: 70px;
    }
    
    @media (max-width: 480px) {
      height: 60px;
    }

    &:hover {
      transform: scale(1.05);
      opacity: 1;
    }
  }

  span {
    background: linear-gradient(120deg, var(--primary-color), var(--primary-light));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 1.2rem;
    display: block;
    white-space: nowrap;
    overflow: visible;
    line-height: 1.2;

    @media (max-width: 1024px) {
      font-size: 0.95rem;
      line-height: 1.1;
      white-space: normal;
    }

    @media (max-width: 768px) {
      font-size: 1rem;
      margin-left: 0.5rem;
      line-height: 1.1;
      white-space: normal;
    }

    @media (max-width: 480px) {
      font-size: 0.85rem;
      line-height: 1.1;
      white-space: normal;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: var(--text-color);
  text-decoration: none;
  font-weight: 500;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, var(--primary-color), var(--primary-light));
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const NavAnchor = styled.a`
  color: var(--text-color);
  text-decoration: none;
  font-weight: 500;
  position: relative;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, var(--primary-color), var(--primary-light));
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 51, 102, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 51, 102, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text-color);
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 100px;
  left: 0;
  right: 0;
  background: rgba(248, 249, 250, 0.98);
  backdrop-filter: blur(10px);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  z-index: 999;
  border-bottom: 1px solid var(--background-dark);
`;

const MobileNavLink = styled(NavLink)`
  font-size: 1.2rem;
  padding: 0.5rem 0;
`;

const MobileNavAnchor = styled(NavAnchor)`
  font-size: 1.2rem;
  padding: 0.5rem 0;
`;

const navContent = {
  de: {
    logo: 'IT-Beratung Reinke',
    home: 'Home',
    about: 'Profil',
    services: 'Leistungen',
    projects: 'Projekte',
    funding: 'Förderung',
    samples: 'Arbeitsproben',
    blog: 'Blog',
    contact: 'Kontakt',
    cta: 'Beratung anfragen',
  },
  en: {
    logo: 'IT-Consulting Reinke',
    home: 'Home',
    about: 'About',
    services: 'Services',
    projects: 'Projects',
    funding: 'Funding',
    samples: 'Work Samples',
    blog: 'Blog',
    contact: 'Contact',
    cta: 'Request Consultation',
  },
};

const Navigation: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language } = useLanguage();
  const t = navContent[language];
  
  const scrollToSection = (sectionId: string) => {
    if (window.location.pathname === '/') {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = `/#${sectionId}`;
    }
  };

  return (
    <>
      <Nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Container>
          <Logo 
            to="/"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              if (window.location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                window.location.href = '/';
              }
            }}
          >
            <img src="/images/IT_Beratung_Reinke_transparent.png" alt="IT-Beratung Reinke" />
            <span>{t.logo}</span>
          </Logo>
          <RightGroup>
            <NavLinks>
              <NavAnchor 
                href="#services" 
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  scrollToSection('services');
                }}
              >
                {t.services}
              </NavAnchor>
              <NavAnchor 
                href="#projects" 
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  scrollToSection('projects');
                }}
              >
                {t.projects}
              </NavAnchor>
              <NavAnchor 
                href="#about" 
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  scrollToSection('about');
                }}
              >
                {t.about}
              </NavAnchor>
              <NavLink to="/foerderung">{t.funding}</NavLink>
              <NavLink to="/samples">{t.samples}</NavLink>
              <NavLink to="/blog">{t.blog}</NavLink>
              <NavLink to="/contact">{t.contact}</NavLink>
              <CTAButton onClick={() => scrollToSection('contact')}>
                {t.cta}
              </CTAButton>
            </NavLinks>
            <LanguageDropdown />
            <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? '✕' : '☰'}
            </MobileMenuButton>
          </RightGroup>
        </Container>
      </Nav>
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >

            <MobileNavAnchor 
              href="#services" 
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                scrollToSection('services');
              }}
            >
              {t.services}
            </MobileNavAnchor>
            <MobileNavAnchor 
              href="#projects" 
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                scrollToSection('projects');
              }}
            >
              {t.projects}
            </MobileNavAnchor>
            <MobileNavAnchor 
              href="#about" 
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                setIsMobileMenuOpen(false);
                scrollToSection('about');
              }}
            >
              {t.about}
            </MobileNavAnchor>
            <MobileNavLink to="/foerderung" onClick={() => setIsMobileMenuOpen(false)}>
              {t.funding}
            </MobileNavLink>
            <MobileNavLink to="/samples" onClick={() => setIsMobileMenuOpen(false)}>
              {t.samples}
            </MobileNavLink>
            <MobileNavLink to="/blog" onClick={() => setIsMobileMenuOpen(false)}>
              {t.blog}
            </MobileNavLink>
            <MobileNavLink to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              {t.contact}
            </MobileNavLink>
            <CTAButton onClick={() => {
              setIsMobileMenuOpen(false);
              scrollToSection('contact');
            }}>
              {t.cta}
            </CTAButton>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation; 